<template>
  <div class="gf-wrapper">
    <!-- 🔹 Filtros principales -->
    <v-card class="gf-filters-card mb-6" elevation="2">
      <!-- Encabezado del panel -->
      <v-card-title class="py-3 d-flex align-center gf-header">
        <div class="d-flex align-center">
          <v-icon class="mr-3" color="blue darken-2">
            mdi-chart-timeline-variant
          </v-icon>

          <div>
            <div class="text-subtitle-1 font-weight-medium">
              Consulta multi-máquina · Hora por Hora
            </div>
            <div class="caption grey--text text--darken-1">
              Selecciona máquinas y un rango de fecha / hora (sin turno) para ver los KPIs consolidados
            </div>
          </div>
        </div>

        <v-spacer />

        <!-- Acciones / resumen rápido (desktop) -->
        <div class="d-none d-md-flex align-center gf-header-actions">
          <v-chip
            v-if="multi.maquinas && multi.maquinas.length"
            small
            class="mr-2"
            color="blue lighten-5"
            text-color="blue darken-2"
          >
            {{ multi.maquinas.length }} máquina(s)
          </v-chip>

          <v-chip
            v-if="multiConsulta.inicio && multiConsulta.fin"
            small
            color="grey lighten-4"
            text-color="grey darken-2"
          >
            {{ multiConsulta.inicio }} → {{ multiConsulta.fin }}
          </v-chip>

          <v-btn
            class="gf-btn-excel ml-3"
            small
            text
            color="green darken-2"
            :disabled="!hayResultado || exportingExcel || uiLocked || loading"
            :loading="exportingExcel"
            @click="descargarExcelMulti"
          >
            <v-icon left small>mdi-file-excel</v-icon>
            Excel
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <!-- Barra de carga mientras consulta multi-rango -->
      <v-progress-linear
        v-if="loadingMulti"
        indeterminate
        color="blue darken-2"
        height="3"
      />

      <v-card-text>
        <!-- Fila principal de filtros -->
        <v-row class="gf-filters-row" align="end">
          <!-- Máquinas (multi) -->
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="multi.maquinas"
              :items="instancias"
              :item-text="nombreMaquina"
              item-value="idMaquinaIns"
              label="Máquinas"
              multiple
              chips
              small-chips
              clearable
              dense
              outlined
              prepend-inner-icon="mdi-factory"
              :loading="loadingInstancias"
              :disabled="uiLocked || loading"
              hide-details="auto"
            />
          </v-col>

          <!-- Fecha/Hora inicio -->
          <v-col cols="12" md="4">
            <MxDateTimePicker
              v-model="multi.inicio"
              label="Fecha / hora inicio"
              :dense="true"
              :disabled="uiLocked || loading"
            />
          </v-col>

          <!-- Fecha/Hora fin -->
          <v-col cols="12" md="4">
            <MxDateTimePicker
              v-model="multi.fin"
              label="Fecha / hora fin"
              :dense="true"
              :disabled="uiLocked || loading"
            />
          </v-col>
        </v-row>

        <!-- Segunda fila: switches + botón consultar (queda más holgado) -->
        <v-row class="mt-3" align="center">
          <v-col cols="12" md="7">
            <!--<div class="d-flex flex-wrap gf-switches">
              <v-switch
                v-model="sinDisponibilidad"
                class="mr-4"
                hide-details
                inset
                dense
                label="Ignorar disponibilidad (A = 100%)"
              />
              <v-switch
                v-model="desConParosNP"
                class="mr-4"
                hide-details
                inset
                dense
                label="Descontar paros no programados del plan"
              />
              <v-switch
                v-model="dispTodosLosParos"
                hide-details
                inset
                dense
                label="Disponibilidad clásica (todos los paros)"
              />
            </div>-->
          </v-col>

          <v-col
            cols="12"
            md="5"
            class="gf-col-btn d-flex justify-end mt-2 mt-md-0"
          >
            <v-btn
              class="gf-btn-consultar mr-2"
              color="blue darken-2"
              depressed
              :loading="loadingMulti"
              :disabled="uiLocked || loading || loadingMulti || !multi.inicio || !multi.fin"
              @click="consultarMultiRango"
            >
              <v-icon left small>mdi-magnify</v-icon>
              <span class="d-none d-md-inline">Consultar</span>
              <span class="d-inline d-md-none">OK</span>
            </v-btn>

            <!-- Excel (en mobile / junto al consultar) -->
            <v-btn
              class="gf-btn-excel d-md-none"
              outlined
              small
              color="green darken-2"
              :disabled="!hayResultado || exportingExcel || uiLocked || loading"
              :loading="exportingExcel"
              @click="descargarExcelMulti"
            >
              <v-icon left small>mdi-file-excel</v-icon>
              Excel
            </v-btn>
          </v-col>
        </v-row>

        <!-- Resumen rápido (mobile) -->
        <div
          class="d-flex d-md-none mt-3 flex-wrap"
          v-if="multi.maquinas.length || (multiConsulta.inicio && multiConsulta.fin)"
        >
          <v-chip
            v-if="multi.maquinas && multi.maquinas.length"
            small
            class="mr-2 mb-2"
            color="blue lighten-5"
            text-color="blue darken-2"
          >
            {{ multi.maquinas.length }} máquina(s)
          </v-chip>

          <v-chip
            v-if="multiConsulta.inicio && multiConsulta.fin"
            small
            class="mb-2"
            color="grey lighten-4"
            text-color="grey darken-2"
          >
            {{ multiConsulta.inicio }} → {{ multiConsulta.fin }}
          </v-chip>
        </div>

        <!-- Estado vacío -->
        <v-alert
          v-if="!hayResultado"
          outlined
          type="info"
          dense
          class="mt-4"
          icon="mdi-timer-sand-empty"
        >
          Selecciona el rango de fecha / hora y da clic en
          <strong>Consultar</strong> para ver los KPIs.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- 🔹 Detalle multi-máquina + KPIs + tabla cruda -->
    <v-card
      v-if="hayResultado"
      class="gf-detail-card"
      outlined
    >
      <v-card-title class="py-3 d-flex align-center">
        <div>
          <div class="subtitle-2 font-weight-medium">
          </div>
          <div class="caption grey--text text--darken-1 mt-1 gf-summary-line">
            {{ resumenDetalle }}
          </div>
        </div>

        <v-spacer />

        <v-chip
          small
          class="mr-2"
          color="blue lighten-5"
          text-color="blue darken-2"
        >
          {{ horaPorHoraMultiHoras.length }} bloque(s) de hora
        </v-chip>

        <v-chip
          small
          color="grey lighten-4"
          text-color="grey darken-2"
        >
          {{ oeePorTurnoDetalleMulti.length }} registro(s) de turno
        </v-chip>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <!-- KPIs del rango -->
        <!--<div class="mb-5">
          <div class="caption grey--text text--darken-1 mb-2">
            KPIs promedio ponderados de los turnos en el rango
          </div>

          <v-row class="gf-kpis-row">
            <v-col cols="12" md="3">
              <v-card outlined class="pa-3 text-center gf-kpi-card">
                <div class="gf-kpi-label">
                  <v-icon x-large class="mr-1" color="blue darken-2">mdi-timer-sand</v-icon>
                  Disponibilidad
                </div>
                <div class="gf-kpi-value">
                  {{ pf(kpisRango.disp) }}
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="3">
              <v-card outlined class="pa-3 text-center gf-kpi-card">
                <div class="gf-kpi-label">
                  <v-icon x-large class="mr-1" color="indigo darken-2">mdi-speedometer</v-icon>
                  Eficiencia
                </div>
                <div class="gf-kpi-value">
                  {{ pf(kpisRango.rend) }}
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="3">
              <v-card outlined class="pa-3 text-center gf-kpi-card">
                <div class="gf-kpi-label">
                  <v-icon x-large class="mr-1" color="green darken-2">mdi-shield-check</v-icon>
                  Calidad
                </div>
                <div class="gf-kpi-value">
                  {{ pf(kpisRango.cal) }}
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="3">
              <v-card outlined class="pa-3 text-center gf-kpi-card">
                <div class="gf-kpi-label">
                  <v-icon x-large class="mr-1" color="teal darken-2">mdi-chart-areaspline</v-icon>
                  OEE
                </div>
                <div class="gf-kpi-value">
                  {{ pf(kpisRango.oee) }}
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>-->

        <!-- Tabla cruda Hora x Hora (misma info que se manda al Excel) -->
        <HoraPorHoraTablaRaw
          v-model="responseMulti"
          :fecha-inicio="multiConsulta.inicio"
          :fecha-fin="multiConsulta.fin"
          :horas-cambio-turno="['18:00', '06:00']"
        />
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import MxDateTimePicker from '../../MxDateTimePicker.vue'
import HoraPorHoraTablaRaw from '../Reportes/ReportesOEE/HoraPorHoraTablaRaw.vue'

// Ajusta este endpoint al nombre que le diste en el backend
const EXCEL_ENDPOINT = '/OEECalculos/XlsHoraPorHoraMulti'

export default {
  name: 'GraficaConFiltros',
  components: {
    MxDateTimePicker,
    HoraPorHoraTablaRaw
  },
  props: {
    uiLocked: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      instancias: [],
      loadingInstancias: false,
      loadingMulti: false,
      exportingExcel: false,

      responseMulti: {},
      sinDisponibilidad: false,
      desConParosNP: true,
      dispTodosLosParos: false,

      horaPorHoraMultiHoras: [],
      oeePorTurnoMulti: [],
      oeePorTurnoDetalleMulti: [],

      multi: {
        maquinas: [],
        inicio: null,
        fin: null
      },
      multiConsulta: {
        inicio: null,
        fin: null
      }
    }
  },

  computed: {
    listaMaquinas () {
      return (this.instancias || []).map(m => ({
        id: m.idMaquinaIns,
        nombre: this.nombreMaquina(m)
      }))
    },
    hayResultado () {
      return this.horaPorHoraMultiHoras && this.horaPorHoraMultiHoras.length > 0
    },
    resumenDetalle () {
      if (!this.hayResultado) return ''
      const maquinasTxt = (this.multi.maquinas && this.multi.maquinas.length)
        ? `${this.multi.maquinas.length} máquina(s)`
        : 'Varias máquinas'
      const rangoTxt = (this.multiConsulta.inicio && this.multiConsulta.fin)
        ? `${this.multiConsulta.inicio} → ${this.multiConsulta.fin}`
        : ''
      const bloquesTxt = `${this.horaPorHoraMultiHoras.length} bloque(s) de hora`
      return [maquinasTxt, rangoTxt, bloquesTxt].filter(Boolean).join(' · ')
    },
    kpisRango () {
      const rows = this.oeePorTurnoMulti || []
      if (!rows.length) {
        return { disp: null, rend: null, cal: null, oee: null }
      }

      let sumTeo = 0
      let sumDisp = 0
      let sumRend = 0
      let sumCal = 0

      rows.forEach(r => {
        const teo = Number(r.teo || 1)
        const d = Number(r.disponibilidad || 0)
        const p = Number(r.rendimiento || 0)
        const c = Number(r.calidad || 0)

        sumTeo += teo
        sumDisp += d * teo
        sumRend += p * teo
        sumCal += c * teo
      })

      if (!sumTeo) {
        return { disp: null, rend: null, cal: null, oee: null }
      }

      const disp = sumDisp / sumTeo
      const rend = sumRend / sumTeo
      const cal = sumCal / sumTeo
      const oee = (disp / 100) * (rend / 100) * (cal / 100) * 100

      return { disp, rend, cal, oee }
    }
  },

  mounted () {
    this.initialize()
  },

  methods: {
    // Helpers básicos
    getMoment () {
      return this.$moment || require('moment')
    },
    parseDT (v) {
      const moment = this.getMoment()
      if (!v) return null
      if (moment.isMoment(v)) return v
      if (v instanceof Date) return moment(v)
      return moment(String(v), ['YYYY-MM-DD HH:mm', moment.ISO_8601], true)
    },
    pf (v) {
      if (v === null || v === undefined || isNaN(v)) return '--'
      return `${Number(v).toFixed(2)}%`
    },

    // Carga inicial de máquinas
    initialize () {
      this.getInstancias()
    },

    nombreMaquina (maquina) {
      if (!maquina) return ''
      return `[${maquina.clave || maquina.nombre}] - ${maquina.marca} ${maquina.modelo}`
    },

    getInstancias () {
      this.loadingInstancias = true
      axios
        .get('/MaquinaInstancias')
        .then(res => {
          this.instancias = Array.isArray(res.data) ? res.data : []
        })
        .catch(err => {
          console.log(err)
          this.instancias = []
        })
        .finally(() => {
          this.loadingInstancias = false
        })
    },

    extraerHoras (data) {
      if (!data) return []
      if (Array.isArray(data)) return data
      if (Array.isArray(data.horas)) return data.horas
      if (Array.isArray(data.Horas)) return data.Horas
      if (Array.isArray(data.horaPorHora)) return data.horaPorHora
      if (Array.isArray(data.HoraPorHora)) return data.HoraPorHora

      const values = Object.values(data)
      for (const v of values) {
        if (
          Array.isArray(v) &&
          v.length &&
          typeof v[0] === 'object' &&
          (
            'fecha' in v[0] ||
            'Fecha' in v[0] ||
            'desdeHora' in v[0] ||
            'hastaHora' in v[0] ||
            'DesdeHora' in v[0] ||
            'HastaHora' in v[0]
          )
        ) {
          return v
        }
      }
      return []
    },

    // 🔹 Consulta multi-rango (mismo EP que ya usas)
    async consultarMultiRango () {
      let { maquinas, inicio, fin } = this.multi

      if (!inicio || !fin) {
        console.warn('Falta inicio o fin en multi.inicio / multi.fin', this.multi)
        return
      }

      if (this.loadingMulti) return

      // Resolver lista de máquinas (si no selecciona, tomamos todas)
      if (!Array.isArray(maquinas) || !maquinas.length) {
        maquinas = (this.instancias || []).map(m => m.idMaquinaIns)
      }

      if (!maquinas.length) {
        console.warn('No hay máquinas seleccionadas ni instancias cargadas')
        return
      }

      this.loadingMulti = true

      try {
        const mInicio = this.parseDT(inicio)
        const mFin = this.parseDT(fin)

        if (!mInicio?.isValid() || !mFin?.isValid()) {
          console.warn('Fechas inválidas para multi-rango', { inicio, fin })
          return
        }
        if (mFin.isBefore(mInicio)) {
          console.warn('Fin antes que inicio', { inicio: mInicio, fin: mFin })
          return
        }

        // Guarda rango real consultado (solo fecha)
        this.multiConsulta.inicio = mInicio.format('YYYY-MM-DD')
        this.multiConsulta.fin = mFin.format('YYYY-MM-DD')

        const inicioStr = mInicio.format('YYYY-MM-DD HH:mm')
        const finStr = mFin.format('YYYY-MM-DD HH:mm')

        const todasHoras = []
        const todosTurnos = []

        for (const idMaquinaIns of maquinas) {
          try {
            const { data } = await axios.get('/OEECalculos/HoraPorHoraRango', {
              params: {
                inicio: inicioStr,
                fin: finStr,
                idMaquinaIns,
                sinDisponibilidad: this.sinDisponibilidad,
                desConParosNP: this.desConParosNP,
                dispTodosLosParos: this.dispTodosLosParos
              }
            })

            const horas = this.extraerHoras(data)

            // etiqueta de la máquina
            const inst = (this.instancias || []).find(
              m => m.idMaquinaIns === idMaquinaIns
            )
            const etiquetaMaquina = inst
              ? this.nombreMaquina(inst)
              : `Máquina ${idMaquinaIns}`

            // oeePorTurno tal cual viene del EP
            const turnos = Array.isArray(data.oeePorTurno)
              ? data.oeePorTurno
              : Array.isArray(data.OeePorTurno)
                ? data.OeePorTurno
                : []

            turnos.forEach(t => {
              todosTurnos.push({
                ...t,
                idMaquinaIns,
                maquinaNombre: etiquetaMaquina
              })
            })

            horas.forEach(h => {
              todasHoras.push({
                ...h,
                idMaquinaIns,
                maquinaNombre: etiquetaMaquina
              })
            })
          } catch (err) {
            console.error(
              'Error en HoraPorHoraRango (multi) para máquina',
              idMaquinaIns,
              err
            )
          }
        }

        this.oeePorTurnoDetalleMulti = todosTurnos

        this.horaPorHoraMultiHoras = todasHoras.sort((a, b) => {
          const fa = String(a.fecha || a.Fecha || '')
          const fb = String(b.fecha || b.Fecha || '')
          if (fa !== fb) return fa.localeCompare(fb)
          const da = String(a.desdeHora || a.DesdeHora || '')
          const db = String(b.desdeHora || b.DesdeHora || '')
          return da.localeCompare(db)
        })

        // Agrupar por fecha + turno (multi-máquina) para KPIs y otros usos
        const agr = new Map()

        for (const t of todosTurnos) {
          const fecha = t.fecha || t.Fecha || null
          const turnoId = t.turnoId ?? t.TurnoId ?? null
          const turnoNombre =
            t.turnoNombre ||
            t.TurnoNombre ||
            (turnoId != null ? `Turno ${turnoId}` : null)

          if (!fecha || !turnoNombre) continue

          const key = `${fecha}|${turnoId ?? ''}|${turnoNombre}`

          const teo = Number(t.piezasTeoricas ?? t.PiezasTeoricas ?? 0)
          const disp = Number(t.disponibilidad ?? t.Disponibilidad ?? 0)
          const rend = Number(
            t.rendimiento ??
            t.Rendimiento ??
            t.eficiencia ??
            t.Eficiencia ??
            0
          )
          const cal = Number(t.calidad ?? t.Calidad ?? 0)
          const oee = Number(t.oee ?? t.Oee ?? t.OEE ?? 0)

          if (!agr.has(key)) {
            agr.set(key, {
              fecha,
              turnoId,
              turnoNombre,
              teo: 0,
              sumOee: 0,
              sumDisp: 0,
              sumRend: 0,
              sumCal: 0
            })
          }

          const a = agr.get(key)
          const peso = teo || 1

          a.teo += peso
          a.sumOee += oee * peso
          a.sumDisp += disp * peso
          a.sumRend += rend * peso
          a.sumCal += cal * peso
        }

        this.oeePorTurnoMulti = Array.from(agr.values()).map(a => ({
          fecha: a.fecha,
          turnoId: a.turnoId,
          turnoNombre: a.turnoNombre,
          teo: a.teo,
          oee: a.teo ? a.sumOee / a.teo : 0,
          disponibilidad: a.teo ? a.sumDisp / a.teo : 0,
          rendimiento: a.teo ? a.sumRend / a.teo : 0,
          calidad: a.teo ? a.sumCal / a.teo : 0
        }))

        this.armarResponseMulti()
      } catch (e) {
        console.error('Error en consultarMultiRango:', e)
        this.horaPorHoraMultiHoras = []
        this.oeePorTurnoMulti = []
        this.oeePorTurnoDetalleMulti = []
        this.responseMulti = { horas: [] }
      } finally {
        this.loadingMulti = false
      }
    },

    armarResponseMulti () {
      this.responseMulti = {
        horas: this.horaPorHoraMultiHoras || [],
        oeePorTurno: this.oeePorTurnoMulti || [],
        oeePorTurnoDetalle: this.oeePorTurnoDetalleMulti || []
      }
    },

    // 🔹 Descarga del Excel (misma info que la tabla)
    async descargarExcelMulti () {
      if (!this.multi.inicio || !this.multi.fin) return
      if (this.exportingExcel) return

      let { maquinas, inicio, fin } = this.multi

      if (!Array.isArray(maquinas) || !maquinas.length) {
        maquinas = (this.instancias || []).map(m => m.idMaquinaIns)
      }
      if (!maquinas.length) return

      const mInicio = this.parseDT(inicio)
      const mFin = this.parseDT(fin)

      if (!mInicio?.isValid() || !mFin?.isValid() || mFin.isBefore(mInicio)) {
        console.warn('Fechas inválidas para Excel multi-rango', { inicio, fin })
        return
      }

      const inicioStr = mInicio.format('YYYY-MM-DD HH:mm')
      const finStr = mFin.format('YYYY-MM-DD HH:mm')

      this.exportingExcel = true
      try {
        const { data } = await axios.get(EXCEL_ENDPOINT, {
          params: {
            inicio: inicioStr,
            fin: finStr,
            // 👇 mando el array tal cual, NO join(',')
            idsMaquinaIns: maquinas,
            sinDisponibilidad: this.sinDisponibilidad,
            desConParosNP: this.desConParosNP,
            dispTodosLosParos: this.dispTodosLosParos
          },
          // 👇 para que axios genere ?idsMaquinaIns=1&idsMaquinaIns=6
          paramsSerializer: params => {
            const sp = new URLSearchParams()
            Object.keys(params).forEach(key => {
              const val = params[key]
              if (Array.isArray(val)) {
                val.forEach(v => sp.append(key, v))
              } else if (val !== undefined && val !== null) {
                sp.append(key, val)
              }
            })
            return sp.toString()
          },
          responseType: 'blob'
        })

        const blob = new Blob(
          [data],
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        )

        const moment = this.getMoment()
        const fileName = `OEE-HoraPorHora-Multi-${mInicio.format('YYYYMMDD-HHmm')}_${mFin.format('YYYYMMDD-HHmm')}.xlsx`

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, fileName)
        } else {
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }
      } catch (err) {
        console.error('Error al descargar Excel multi-rango', err)
      } finally {
        this.exportingExcel = false
      }
    }
  }
}
</script>

<style scoped>
.gf-wrapper {
  max-width: 100%;
  margin: 0 auto;
}

/* Tarjeta de filtros */
.gf-filters-card {
  border-radius: 16px;
}

.gf-header {
  flex-wrap: wrap;
}

.gf-header-actions > * + * {
  margin-left: 8px;
}

/* Fila de filtros un poco más aireada */
.gf-filters-row {
  align-items: flex-end;
}

.gf-switches > * {
  margin-bottom: 4px;
}

/* Col del botón, para poder controlar en responsive */
.gf-col-btn {
  min-height: 40px;
}

/* Botón pill bonito */
.gf-btn-consultar {
  border-radius: 999px;
  padding: 0 20px;
  min-width: 120px;
  height: 40px;
  text-transform: none;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(33, 150, 243, 0.35);
}

/* Botón Excel */
.gf-btn-excel {
  border-radius: 999px;
  text-transform: none;
  font-weight: 500;
}

/* En móvil que se vea ancho completo */
@media (max-width: 960px) {
  .gf-col-btn {
    margin-top: 8px;
  }

  .gf-btn-consultar {
    width: 100%;
    justify-content: center;
  }
}

/* Botón deshabilitado: texto gris y fondo gris claro */
.gf-btn-consultar.v-btn--disabled {
  color: #9e9e9e !important;
  background-color: #eeeeee !important;
  box-shadow: none;
}

/* Tarjeta de detalle */
.gf-detail-card {
  border-radius: 16px;
  padding-bottom: 8px;
}

.gf-summary-line {
  max-width: 480px;
}

/* KPIs */
.gf-kpis-row .v-col {
  margin-bottom: 12px;
}

.gf-kpi-card {
  border-radius: 14px;
}

.gf-kpi-label {
  font-size: 32px;   /* ajusta a gusto */
  text-transform: uppercase;
}

.gf-kpi-value {
  font-size: 30px;   /* más grande o más chico */
  font-weight: 700;
}
</style>